import React from 'react';

import PropTypes from 'prop-types';

import { AddressSearchComboBox } from 'components/FormHelper/AddressInput/AddressSearchComboBox';
import { ComboBox } from 'components/FormHelper/ComboBox/ComboBox';
import { FormField } from 'components/FormHelper/FormField';
import { InputField } from 'components/FormHelper/InputField';
import { InputRow } from 'components/FormHelper/InputRow';

import { prepValueForDisplay } from 'utils/DataUtils';

import { COMPANY_FIELDS } from './addressInput.constants';
import { useAddressInputController } from './useAddressInputController';

const resolvePostalCityDisplay = (values = {}) => {
  const { companyPostal, companyCity } = values;
  if (companyPostal && companyCity) {
    return `${companyPostal} - ${companyCity}`;
  }
  return prepValueForDisplay(companyPostal || companyCity);
};

const AddressInputReadOnly = ({ values }) => (
  <div className="address-input">
    <InputRow>
      <FormField
        name={COMPANY_FIELDS.POSTAL_CITY_ID}
        formik={null}
        label="PLZ / Ort"
        value={resolvePostalCityDisplay(values)}
        oldValue=""
        fullWidth
        disabled
        readOnly
        isEdited={false}
      />
      <FormField
        name={COMPANY_FIELDS.DISTRICT_ID}
        formik={null}
        label="Ortsteil"
        value={prepValueForDisplay(values[COMPANY_FIELDS.DISTRICT])}
        oldValue=""
        fullWidth
        disabled
        readOnly
        isEdited={false}
      />
    </InputRow>
    <InputRow>
      <FormField
        name={COMPANY_FIELDS.STREET_ID}
        formik={null}
        label="Straße"
        value={prepValueForDisplay(values[COMPANY_FIELDS.STREET])}
        oldValue=""
        fullWidth
        disabled
        readOnly
        isEdited={false}
      />
      <FormField
        name={COMPANY_FIELDS.HOUSE_NUMBER}
        formik={null}
        label="Hausnummer"
        value={prepValueForDisplay(values[COMPANY_FIELDS.HOUSE_NUMBER])}
        oldValue=""
        fullWidth
        disabled
        readOnly
        isEdited={false}
      />
      <FormField
        name={COMPANY_FIELDS.HOUSE_NUMBER_ADDITION}
        formik={null}
        label="Hausnummerzusatz"
        value={prepValueForDisplay(values[COMPANY_FIELDS.HOUSE_NUMBER_ADDITION])}
        oldValue=""
        fullWidth
        disabled
        readOnly
        isEdited={false}
      />
    </InputRow>
  </div>
);

AddressInputReadOnly.propTypes = {
  values: PropTypes.object,
};

AddressInputReadOnly.defaultProps = {
  values: {},
};

const AddressInputInteractive = ({ formik, showErrorsWhenTouched }) => {
  const { comboProps, fieldProps, loading, handlers, flags } = useAddressInputController({
    formik,
    showErrorsWhenTouched,
  });

  return (
    <div className="address-input">
      <InputRow>
        <AddressSearchComboBox
          {...comboProps.postalCity}
          loading={loading.city}
          onInputChange={handlers.handlePostalCityInputChange}
          onChange={handlers.handlePostalCityChange}
          disabled={!flags.hasSelectedCountry}
          oldValue={formik.initialValues.companyPostalCity}
        />
        <ComboBox
          {...comboProps.district}
          disabled={!flags.hasSelectedCountry}
          readOnlyInput
          onChange={handlers.handleDistrictChange}
          onInputChange={handlers.handleDistrictInputChange}
          oldValue={formik.initialValues.companyRegionId}
        />
      </InputRow>
      <InputRow>
        <AddressSearchComboBox
          {...comboProps.street}
          onChange={handlers.handleStreetChange}
          onInputChange={handlers.handleStreetInputChange}
          loading={loading.street}
          disabled={!flags.hasSelectedCountry || !flags.hasSelectedPostalCity}
          oldValue={formik.initialValues.companyStreetId}
        />
        <InputField
          {...fieldProps.houseNumber}
          disabled={!flags.hasSelectedCountry || !flags.hasSelectedStreet}
          oldValue={formik.initialValues.companyHouseNumber}
        />
        <InputField
          {...fieldProps.houseNumberAddition}
          disabled={!flags.hasSelectedCountry || !flags.hasSelectedHouseNumber}
          oldValue={formik.initialValues.companyHouseNumberAddition}
        />
      </InputRow>
    </div>
  );
};

AddressInputInteractive.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.object,
    errors: PropTypes.object,
    handleChange: PropTypes.func,
    setFieldValue: PropTypes.func,
    initialValues: PropTypes.object,
  }).isRequired,
  showErrorsWhenTouched: PropTypes.bool,
};

AddressInputInteractive.defaultProps = {
  showErrorsWhenTouched: false,
};

export const AddressInput = ({ formik, readOnly, values, showErrorsWhenTouched }) => {
  if (readOnly) {
    return <AddressInputReadOnly values={values} />;
  }

  return <AddressInputInteractive formik={formik} showErrorsWhenTouched={showErrorsWhenTouched} />;
};

AddressInput.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.object,
    errors: PropTypes.object,
    handleChange: PropTypes.func,
    setFieldValue: PropTypes.func,
    initialValues: PropTypes.object,
  }),
  readOnly: PropTypes.bool,
  values: PropTypes.object,
  showErrorsWhenTouched: PropTypes.bool,
};

AddressInput.defaultProps = {
  formik: null,
  readOnly: false,
  values: {},
  showErrorsWhenTouched: false,
};
